// Modal dialog (Radix). Focus trap, focus return to the trigger, Escape, scroll lock and the
// aria-labelledby title come from Radix; always render a DialogTitle (sr-only if need be) and a
// DialogDescription or `aria-describedby={undefined}`.
// Mantine layers inside it and Lumen layers over a Mantine Modal both work: see the rules at the top of
// overlay-coexistence.tsx. Centred with inset-0 + m-auto, never a translate (rule 1 there).
import { XIcon } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import type * as React from 'react';
import { useRef } from 'react';

import { Button } from '@/components/lumen/button';
import { MantineLayerHost, layerGuards } from '@/components/lumen/overlay-coexistence';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-500 bg-scrim data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  );
}

/**
 * Radix returns focus to a DialogTrigger only. For a dialog opened from code (a ⌘K palette, a row
 * action), return it to whatever had focus when it opened. Shared with SheetContent.
 */
function useFocusReturn({
  onOpenAutoFocus,
  onCloseAutoFocus,
}: Pick<React.ComponentProps<typeof DialogPrimitive.Content>, 'onOpenAutoFocus' | 'onCloseAutoFocus'>) {
  const previous = useRef<HTMLElement | null>(null);
  return {
    onOpenAutoFocus: (event: Event) => {
      // Runs before Radix moves focus into the content.
      previous.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      onOpenAutoFocus?.(event);
    },
    onCloseAutoFocus: (event: Event) => {
      onCloseAutoFocus?.(event);
      const el = previous.current;
      previous.current = null;
      if (event.defaultPrevented || !el || el === document.body || !el.isConnected) return;
      event.preventDefault();
      el.focus();
    },
  };
}

// Shared with SheetContent's close button.
const closeButtonClasses =
  "absolute top-3 right-3 inline-flex size-7 items-center justify-center rounded-control text-fg-muted outline-none transition-colors hover:bg-tint hover:text-fg focus-visible:ring-[3px] focus-visible:ring-label/40 disabled:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0";

function DialogContent({
  className,
  children,
  showCloseButton = true,
  onEscapeKeyDown,
  onPointerDownOutside,
  onFocusCapture,
  onBlurCapture,
  onKeyDownCapture,
  onOpenAutoFocus,
  onCloseAutoFocus,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
}) {
  const { t } = useLanguage();
  const focusReturn = useFocusReturn({ onOpenAutoFocus, onCloseAutoFocus });
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'fixed inset-0 z-500 m-auto grid h-fit max-h-[calc(100%-2rem)] w-full max-w-[calc(100%-2rem)] gap-4 overflow-y-auto rounded-card border border-line-strong bg-surface p-6 text-fg shadow-overlay outline-none data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=open]:duration-200 data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:max-w-lg',
          className
        )}
        {...layerGuards({ onEscapeKeyDown, onPointerDownOutside, onFocusCapture, onBlurCapture, onKeyDownCapture })}
        {...focusReturn}
        {...props}
      >
        <MantineLayerHost>
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close data-slot="dialog-close" className={closeButtonClasses}>
              <XIcon />
              <span className="sr-only">{t('lumen.close')}</span>
            </DialogPrimitive.Close>
          )}
        </MantineLayerHost>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">{t('lumen.close')}</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('font-semibold text-base text-fg leading-6', className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-fg-muted', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  closeButtonClasses,
  useFocusReturn,
};
